import { useCallback } from 'react';
import { Form } from 'ALIASED-antd';
import type { ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import type { SessionForm } from '@components/Clinicians/session-form';
import ClinicianSessionFormState from '@components/Clinicians/Session/hooks/clinicianSessionFormState';

import {
  ACTION_SUBTYPE_KEY,
  ACTION_AUTH_KEY,
  ACTION_COMMENT_KEY,
  ACTION_SPECIALITY_KEY,
  ACTION_SUBSPECIALITY_KEY,
  ACTION_REASON_KEY,
  ACTION_POSTCODE_KEY,
  ACTION_OTHER_REASON_KEY,
  ACTION_URGENCY_KEY,
  ACTION_REFCLI_KEY,
  ACTION_DISCHARGE_REASON_KEY,
  ACTION_REFF2F_CODE,
  ACTION_DISCHG_CODE,
  ACTION_BKVCON_CODE,
  ACTION_AUTH_START_KEY,
  ACTION_REFER_SELF_KEY,
  ACTION_CALL_TYPE_KEY
} from '@components/Clinicians/session-form';
import { useSessionAppointmentId } from './useSessionAppointmentId';

/* The sesson form keys that must be explicitly cleared when a given ourcome group is deselected */
const OUTCOME_GROUP_WIPE_KEYS: Record<ApptActionCodes, (keyof SessionForm)[]> = {
  BKVCON: [
    `${ACTION_AUTH_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_AUTH_START_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_SPECIALITY_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_SUBSPECIALITY_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_REFER_SELF_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_COMMENT_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_REASON_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_CALL_TYPE_KEY}_${ACTION_BKVCON_CODE}`,
    `${ACTION_SUBTYPE_KEY}_${ACTION_BKVCON_CODE}`
  ],
  REFF2F: [
    `${ACTION_COMMENT_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_POSTCODE_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_OTHER_REASON_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_REFCLI_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_REASON_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_SPECIALITY_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_SUBSPECIALITY_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_URGENCY_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_SUBTYPE_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_AUTH_KEY}_${ACTION_REFF2F_CODE}`,
    `${ACTION_AUTH_START_KEY}_${ACTION_REFF2F_CODE}`
  ],
  DISCHG: [`${ACTION_DISCHARGE_REASON_KEY}_${ACTION_DISCHG_CODE}`],
  PRESCR: ['filteredMedications', 'generalAdvice', 'prescribeMedication', 'tableMedications'],
  INVREQ: ['investigations', 'refetchInvestigationRequests', 'request'],
  REF2AE: [],
  TRCARE: [],
  PTFACT: []
};

export const useClearSessionFormKeys = () => {
  const form = Form.useFormInstance<SessionForm>();
  const appointmentId = useSessionAppointmentId(true);

  return useCallback(
    (appt_action_code: ApptActionCodes) => {
      const keys = OUTCOME_GROUP_WIPE_KEYS[appt_action_code];
      keys.forEach((key) => {
        form.setFieldValue(key, undefined);

        ClinicianSessionFormState.clear(key, appointmentId);
      });
      form.validateFields();
    },

    [appointmentId, form]
  );
};
